import { useMemo, useRef } from "react";
import type { CSSObjectWithLabel } from "react-select";

// Výchozí `minMenuHeight` react-selectu; předáváme ho Selectu výslovně, aby se hodnoty nemohly rozejít
export const MIN_MENU_HEIGHT = 140;
// Výchozí `theme.spacing.menuGutter` react-selectu (baseUnit 4 * 2), jeho `menu` ho dává do marginTop/marginBottom
const MENU_GUTTER = 8;
// `getMenuPlacement` bere místo nad ovladačem jako `containerTop - marginTop`, proto práh pro `top` o mezeru roste
export const MIN_SPACE_FOR_TOP_MENU = MIN_MENU_HEIGHT + MENU_GUTTER;

const isOutsideMenuList = (event: Event) =>
  !(event.target instanceof Element && event.target.closest(".react-select__menu-list"));

// Bez typu `StylesConfig`, aby šly styly předat Selectu s libovolným typem možnosti
const styles = {
  menuPortal: (base: CSSObjectWithLabel) => ({ ...base, zIndex: 9999 }),
  menuList: (base: CSSObjectWithLabel) => ({ ...base, overscrollBehavior: "contain" as const }),
};

/**
 * Společné nastavení fixní nabídky react-selectu (portál do `body`) pro výběry v úlohách.
 *
 * AIDEV-NOTE: Nabídka je `menuPosition="fixed"` a výška se jí počítá jen při otevření, takže po scrollu stránky by
 * sjela mimo obrazovku. Proto ji při scrollu zavíráme. `closeMenuOnScroll={true}` ale nestačí: react-select
 * (Select.onScroll) zavírá jen když je `event.target` HTMLElement a zároveň html/body, kdežto scroll stránky má za
 * target `document` a nezavře nic. Funkce zavře nabídku při scrollu mimo její seznam (i v modalu), uvnitř ji nechá.
 * Posluchač scrollu má každý Select registrovaný stále, proto vrací true jen u otevřené nabídky (ref, ne state, ať
 * nevznikají rendery). `maxMenuHeight` zůstává obří, aby se krátký seznam vešel celý (TD-256); `menuList` smí přebít
 * jen `overscrollBehavior`, NE `maxHeight`/`overflowY`, jinak by nízká obrazovka znemožnila výběr spodních možností
 * (react-select u `fixed` menu omezí výšku na místo ve viewportu a menu se uvnitř scrolluje). [TD-485]
 */
export const useFixedMenu = (onOpen?: () => void) => {
  const isOpen = useRef(false);

  return useMemo(
    () => ({
      menuPosition: "fixed" as const,
      menuPortalTarget: typeof document !== "undefined" ? document.body : undefined,
      maxMenuHeight: 9999,
      minMenuHeight: MIN_MENU_HEIGHT,
      closeMenuOnScroll: (event: Event) => isOpen.current && isOutsideMenuList(event),
      onMenuOpen: () => {
        isOpen.current = true;
        onOpen?.();
      },
      onMenuClose: () => {
        isOpen.current = false;
      },
      styles,
    }),
    [onOpen],
  );
};
